// src/utils/imageFit.js
//
// Keep manuscript images inside the page's text column.
//
// Images are inserted up to 600px wide, which suits US Letter (a 720px column at
// the default margins) and nothing smaller: a 1/4 Letter book has a 312px
// column, so a 600px image hung off the side of the page and was cut off there
// — and so was every piece of one running onto the next page. An image wider
// than the column is scaled down to fit it, keeping its shape. A narrower one
// is left exactly as the author sized it.

const IMAGE_SEL = '.rv-resizable-image';

// Wrapped images carry a 16px side margin (see insertImage in EditorPage.jsx),
// which has to fit in the column too.
const WRAP_MARGIN_PX = 16;

const isWrapped = (span) => {
  const f = span?.style?.float;
  return f === 'left' || f === 'right';
};

/**
 * The width of a page body's text column in CSS px: its box less its left and
 * right padding (the page margins). Unaffected by zoom.
 *
 * @param {Element|null} body a .rv-page-body
 * @returns {number} 0 when there is no body
 */
export function columnWidthOf(body) {
  if (!body) return 0;
  const cs = window.getComputedStyle(body);
  const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  return Math.max(0, body.clientWidth - pad);
}

/**
 * Scale `span` down to fit a column `columnWidth` px wide, keeping its shape.
 * Sets the inline width and height and the `data-width` / `data-height` the
 * editor keeps them in. A cropped piece of a running image is left alone: the
 * reflow joins the pieces back into one image before it fits and cuts again.
 *
 * @param {Element} span a .rv-resizable-image
 * @param {number} columnWidth from `columnWidthOf`
 * @returns {boolean} whether it was resized
 */
export function fitImageToColumn(span, columnWidth) {
  if (!span || !(columnWidth > 0) || span.hasAttribute('data-bleed-offset')) return false;
  const limit = Math.floor(columnWidth - (isWrapped(span) ? WRAP_MARGIN_PX : 0));
  const width = parseFloat(span.getAttribute('data-width')) || parseFloat(span.style.width) || span.offsetWidth;
  const height = parseFloat(span.getAttribute('data-height')) || parseFloat(span.style.height) || span.offsetHeight;
  if (!(width > limit) || !(height > 0) || limit <= 0) return false;

  const newHeight = (height * limit) / width;
  span.style.width = `${limit}px`;
  span.setAttribute('data-width', String(limit));
  span.setAttribute('data-height', String(newHeight));
  span.style.height = `${newHeight}px`;
  return true;
}

// Room an image needs above and below itself on a page: its own 12px margins
// and its paragraph's 12px bottom margin, with a little to spare.
const IMAGE_VERTICAL_ALLOWANCE_PX = 48;

/**
 * The height of a page body's text area in CSS px: its box less its top and
 * bottom padding.
 *
 * @param {Element|null} body a .rv-page-body
 * @returns {number} 0 when there is no body
 */
export function columnHeightOf(body) {
  if (!body) return 0;
  const cs = window.getComputedStyle(body);
  const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  return Math.max(0, body.clientHeight - pad);
}

/**
 * Fit a freshly inserted image to its page: within the column's width, and
 * short enough to sit whole on one page, keeping its shape. Only for a new
 * insert — an image the author has sized keeps its height, and one taller than
 * a page can be set to continue onto the next.
 *
 * @param {Element} span the new .rv-resizable-image
 * @param {Element|null} body the .rv-page-body it went into
 * @returns {boolean} whether it was resized
 */
export function fitNewImageToPage(span, body) {
  if (!span || !body) return false;
  let resized = fitImageToColumn(span, columnWidthOf(body));
  const limit = columnHeightOf(body) - IMAGE_VERTICAL_ALLOWANCE_PX;
  const width = parseFloat(span.getAttribute('data-width')) || parseFloat(span.style.width) || 0;
  const height = parseFloat(span.getAttribute('data-height')) || parseFloat(span.style.height) || 0;
  if (limit > 0 && width > 0 && height > limit) {
    const newWidth = (width * limit) / height;
    span.style.width = `${newWidth}px`;
    span.style.height = `${limit}px`;
    span.setAttribute('data-width', String(newWidth));
    span.setAttribute('data-height', String(limit));
    resized = true;
  }
  return resized;
}

/**
 * Fit every image in `blocks` to the column.
 *
 * @param {Element[]} blocks page-body children
 * @param {number} columnWidth from `columnWidthOf`
 * @returns {number} how many were resized
 */
export function fitImagesToColumn(blocks, columnWidth) {
  let resized = 0;
  for (const block of blocks) {
    const spans = block.matches?.(IMAGE_SEL) ? [block] : Array.from(block.querySelectorAll?.(IMAGE_SEL) || []);
    for (const span of spans) if (fitImageToColumn(span, columnWidth)) resized += 1;
  }
  return resized;
}
